// components/careers/CareerForm.jsx
import React, { useState, useEffect } from "react";
import { Form, Button, InputGroup, Spinner, Image } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  createCareerAction,
  updateCareerAction,
} from "../../pages/careers/CareerAction";
import { CustomInput } from "../custom-input/CustomInput";

const initialState = {
  title: "",
  location: "",
  employmentType: "Full-time",
  salaryRange: { min: "", max: "", currency: "USD" },
  description: "",
  responsibilities: [""],
  requirements: [""],
  niceToHave: [""],
  benefits: [""],
  howToApply: "",
  company: { name: "", website: "", logo: "", about: "" },
  status: "open",
};

export const CareerForm = ({
  careerId,
  initialData,
  selectedCareer,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  // Seed form from initial/fetched data
  useEffect(() => {
    if (careerId && initialData?._id === careerId) {
      setForm({ ...initialState, ...initialData });
    } else if (!careerId) {
      setForm(initialState);
    }
  }, [careerId, initialData]);

  useEffect(() => {
    if (careerId && selectedCareer?._id === careerId) {
      setForm((prev) => ({ ...prev, ...selectedCareer }));
    }
  }, [careerId, selectedCareer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, e, index) => {
    const updated = [...form[field]];
    updated[index] = e.target.value;
    setForm((prev) => ({ ...prev, [field]: updated }));
  };

  const handleAddArrayField = (field) => {
    setForm((prev) => ({ ...prev, [field]: [...prev[field], ""] }));
  };

  const handleRemoveArrayField = (field, index) => {
    const updated = form[field].filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, [field]: updated }));
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      company: { ...prev.company, [name]: value },
    }));
  };

  const handleSalaryChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      salaryRange: { ...prev.salaryRange, [name]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (careerId) {
        await dispatch(updateCareerAction(careerId, form));
      } else {
        await dispatch(createCareerAction(form));
      }
      onClose?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {submitting && <Spinner animation="border" size="sm" className="mb-3" />}

      <CustomInput
        name="title"
        label="Job Title*"
        value={form.title}
        onChange={handleChange}
        required
      />

      <CustomInput
        name="location"
        label="Location*"
        value={form.location}
        onChange={handleChange}
        required
      />

      <Form.Group className="mb-3">
        <Form.Label>Employment Type*</Form.Label>
        <Form.Select
          name="employmentType"
          value={form.employmentType}
          onChange={handleChange}
          required
        >
          <option>Full-time</option>
          <option>Part-time</option>
          <option>Contract</option>
          <option>Internship</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Salary Range</Form.Label>
        <InputGroup className="mb-2">
          <Form.Control
            type="number"
            placeholder="Min"
            name="min"
            value={form.salaryRange.min}
            onChange={handleSalaryChange}
          />
          <Form.Control
            type="number"
            placeholder="Max"
            name="max"
            value={form.salaryRange.max}
            onChange={handleSalaryChange}
          />
          <Form.Control
            type="text"
            placeholder="Currency"
            name="currency"
            value={form.salaryRange.currency}
            onChange={handleSalaryChange}
          />
        </InputGroup>
      </Form.Group>

      <CustomInput
        name="description"
        label="Job Description*"
        as="textarea"
        rows={4}
        value={form.description}
        onChange={handleChange}
        required
      />

      {["responsibilities", "requirements", "niceToHave", "benefits"].map(
        (field) => (
          <Form.Group className="mb-3" key={field}>
            <Form.Label>
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </Form.Label>
            {form[field].map((item, idx) => (
              <InputGroup className="mb-2" key={idx}>
                <Form.Control
                  type="text"
                  placeholder={`Enter ${field.slice(0, -1)}`}
                  value={item}
                  onChange={(e) => handleArrayChange(field, e, idx)}
                />
                <Button
                  variant="outline-danger"
                  onClick={() => handleRemoveArrayField(field, idx)}
                >
                  🗑
                </Button>
              </InputGroup>
            ))}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleAddArrayField(field)}
            >
              ➕ Add {field.slice(0, -1)}
            </Button>
          </Form.Group>
        )
      )}

      <CustomInput
        name="howToApply"
        label="How to Apply*"
        as="textarea"
        rows={2}
        value={form.howToApply}
        onChange={handleChange}
        required
      />

      <h6>Company Info</h6>
      {["name", "website", "logo", "about"].map((field) => (
        <CustomInput
          key={field}
          name={field}
          label={field.charAt(0).toUpperCase() + field.slice(1)}
          value={form.company[field]}
          onChange={handleCompanyChange}
        />
      ))}

      <Form.Group className="mb-3">
        <Form.Label>Status</Form.Label>
        <Form.Select name="status" value={form.status} onChange={handleChange}>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </Form.Select>
      </Form.Group>

      <div className="d-grid py-3">
        <Button type="submit" disabled={submitting}>
          {careerId ? "Update Career" : "Submit Career"}
        </Button>
      </div>
    </Form>
  );
};
